import React from 'react';
import { useSelector } from 'react-redux';
import { CONTAINED_VARIANT, GHOST_VARIANT, NO_ICON, SIZE_44 } from '@yme/atoms/button';
import { Col, Container, Row } from '@yme/atoms/grid';
import { useWindowSize } from '@yme/hooks/index';
import { getCurrentNavigationLink, getRTL } from '@yme/selectors/index';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreBlack700, colorCoreGray100 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel1, RegularTextLevel3 } from '@yme/tokens/typography';
import { NewsletterStatus } from './constants';
import { getNewsletterQueryParams, getNewsletterSubscriptionLink } from './helpers';
import { BackgroundImageStyled, ButtonLinkStyled } from './styled';
export const NewsletterSubscriptionConfirmation = ({ backgroundImage, subscriptionsLink, subscriptionManagerLink, }) => {
    const homePageLink = useSelector(getCurrentNavigationLink);
    const isRightToLeft = useSelector(getRTL);
    const { isMobileView } = useWindowSize();
    const { t } = useTranslation();
    const { status, language, individualId } = getNewsletterQueryParams();
    const getContent = () => {
        switch (status) {
            case NewsletterStatus.EXPIRED:
                return {
                    title: t('newsletter_confirmationRetention_header'),
                    description: t('newsletter_confirmationRetention_message'),
                };
            case NewsletterStatus.REPEATED_CONFIRMATION:
                return {
                    title: t('newsletter_confirmationSecondClick_header'),
                    description: t('newsletter_confirmationSecondClick_message'),
                };
            case NewsletterStatus.FAILED:
                return {
                    title: t('newsletter_confirmationFailure_header'),
                    description: t('newsletter_confirmationFailure_message'),
                };
            case NewsletterStatus.CONFIRMED:
            default:
                return {
                    title: t('newsletter_confirmationBasic_header'),
                    description: t('newsletter_confirmationBasic_message'),
                };
        }
    };
    const { title, description } = getContent();
    const CTABlock = (React.createElement(FlexWrapper, { gap: "16px", mtDesktop: "16px", mtTablet: "8px", flexDirection: isMobileView ? 'column' : 'row' },
        status !== NewsletterStatus.FAILED && (React.createElement(ButtonLinkStyled, { isDarkMode: true, buttonLink: getNewsletterSubscriptionLink(subscriptionsLink, subscriptionManagerLink, language, encodeURIComponent(individualId || '')), buttonLabel: t('newsletter_confirmationMySubscriptions_button'), hasIconButton: NO_ICON, size: SIZE_44, buttonVariant: CONTAINED_VARIANT, buttonWidth: isMobileView ? '100%' : 'auto' })),
        React.createElement(ButtonLinkStyled, { isDarkMode: true, buttonLink: homePageLink, buttonLabel: t('newsletter_confirmationHomePage_button'), hasIconButton: NO_ICON, size: SIZE_44, buttonVariant: GHOST_VARIANT, buttonWidth: isMobileView ? '100%' : 'auto' })));
    if (!backgroundImage || !subscriptionsLink || !subscriptionManagerLink) {
        return null;
    }
    if (!status || !Object.values(NewsletterStatus).includes(status)) {
        return React.createElement(BackgroundImageStyled, { ...backgroundImage });
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(BackgroundImageStyled, { ...backgroundImage },
            React.createElement(Container, { isRightToLeft: isRightToLeft, height: "100%" },
                React.createElement(Row, { height: "100%" },
                    React.createElement(Col, { md: 7, lg: 6 },
                        React.createElement(FlexWrapper, { height: "100%", flexDirection: "column", justifyContent: "flex-end", gap: "16px", pbMobile: "16px", pbTablet: "32px", pbDesktop: "120px" },
                            React.createElement(HeadingLevel1, { color: colorCoreGray100 }, title),
                            !isMobileView && (React.createElement(React.Fragment, null,
                                React.createElement(RegularTextLevel3, { color: colorCoreGray100 }, description),
                                status !== NewsletterStatus.EXPIRED && CTABlock))))))),
        isMobileView && (React.createElement(Container, { bgColor: colorCoreBlack700 },
            React.createElement(Row, null,
                React.createElement(Col, null,
                    React.createElement(FlexWrapper, { padding: "24px 0px", flexDirection: "column", gap: "24px" },
                        React.createElement(RegularTextLevel3, { color: colorCoreGray100 }, description),
                        status !== NewsletterStatus.EXPIRED && CTABlock)))))));
};
